/*
 * cart-line-merge.js
 *
 * Optis (BSS Product Options) appends a hidden `__` line-item property with a
 * random value to every add-to-cart request (it patches the network call, so
 * the theme cannot strip it before submit). Shopify only merges cart lines
 * whose properties match exactly, so adding the same product with the same
 * options twice always produced two cart lines.
 *
 * This module merges such duplicates after the fact: lines with the same
 * variant and identical properties EXCEPT `__` are combined into the first
 * line (quantities summed). Called by product-form.js after each successful
 * add; also runs once on cart-page load to clean up carts that already
 * contain duplicates, reloading the page when it changed anything.
 */
(function () {
  'use strict';

  var merging = false;
  var queued = false;

  // Properties that vary per add without carrying real configuration:
  // `__` (Optis' random token) and `_bndlr` (Bundler's tracking marker —
  // discounts are recomputed from quantity at checkout). Price-relevant
  // hidden properties like __bss_po_addons stay significant.
  var IGNORED_PROPS = { __: true, _bndlr: true };

  function groupKey(item) {
    var props = {};
    Object.keys(item.properties || {}).forEach(function (k) {
      if (!IGNORED_PROPS[k]) props[k] = item.properties[k];
    });
    var sorted = Object.keys(props).sort().map(function (k) {
      return k + ':' + props[k];
    });
    var sellingPlan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
      ? item.selling_plan_allocation.selling_plan.id
      : '';
    return item.variant_id + '|' + sellingPlan + '|' + sorted.join('|');
  }

  // Full-property signature, INCLUDING the tracking tokens. Two lines with an
  // identical full signature were split by an app's cart transform (Bundler
  // does this for volume-discount allocation) — Shopify itself would have
  // merged them otherwise. Those splits are intentional: merging them starts
  // a fight with the transform that re-splits, and quantities get lost.
  function fullSig(item) {
    var props = item.properties || {};
    var sorted = Object.keys(props).sort().map(function (k) {
      return k + ':' + props[k];
    });
    return item.variant_id + '|' + sorted.join('|');
  }

  // One positional /cart/update.js call for the whole merge. Line keys are
  // useless here: Optis intercepts per-line /cart/change.js requests and
  // regenerates its `__` token, which changes the surviving line's key mid-
  // sequence (second call 400s and quantities get lost). Positional updates
  // reference lines by index, immune to key rewrites, and apply atomically.
  function updateAll(quantities) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: quantities }),
    });
  }

  // After a merge, the cart drawer (if present) still shows the pre-merge
  // lines; re-render it and the header bubble via the Section Rendering API.
  // Preserves the drawer's open/closed state: only inner content is replaced.
  function refreshDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return Promise.resolve();
    return fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then(function (res) { return res.json(); })
      .then(function (sections) {
        var drawerEl = document.getElementById('CartDrawer');
        if (drawerEl && sections['cart-drawer']) {
          var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          var fresh = doc.querySelector('#CartDrawer');
          if (fresh) {
            drawerEl.innerHTML = fresh.innerHTML;
            var overlay = drawerEl.querySelector('#CartDrawer-Overlay');
            if (overlay && typeof drawer.close === 'function') {
              overlay.addEventListener('click', drawer.close.bind(drawer));
            }
          }
        }
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble && sections['cart-icon-bubble']) {
          var bdoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          var bnode = bdoc.querySelector('.shopify-section');
          bubble.innerHTML = bnode ? bnode.innerHTML : sections['cart-icon-bubble'];
        }
      })
      .catch(function () {});
  }

  // Tell the theme's cart surfaces (cart page items, drawer) to re-render so
  // no quantity button keeps a stale line index — clicking one after the
  // merge shrank the cart would otherwise 400 with "Parameter line is
  // ongeldig". Uses the theme's pubsub when present.
  function notifySurfaces() {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-line-merge' }); } catch (e) { /* fall through */ }
    }
    return refreshDrawer();
  }

  function getCart() {
    return fetch('/cart.js').then(function (res) { return res.json(); });
  }

  // Fingerprint of the cart's line structure. Two equal consecutive
  // fingerprints mean the app watchers (Optis token rewrites, Bundler's
  // bundler-cart-call changes) have settled.
  function cartSig(cart) {
    return cart.item_count + '|' + cart.items.map(function (i) { return i.key + ':' + i.quantity; }).join('|');
  }

  // Resolve with a cart snapshot only when the cart has been stable for one
  // interval; null when it never settles within the attempts budget.
  function stableCart(attempt) {
    return getCart().then(function (first) {
      return new Promise(function (resolve) { setTimeout(resolve, 800); }).then(function () {
        return getCart().then(function (second) {
          if (cartSig(first) === cartSig(second)) return second;
          if (attempt >= 5) return null;
          return stableCart(attempt + 1);
        });
      });
    });
  }

  // Returns a promise resolving to true when lines were merged.
  //
  // Optis and Bundler both mutate the cart asynchronously right after an add
  // (token rewrites, watcher fixups). Merging while that is still in flight
  // loses units: our positional update overwrites their concurrent rewrite
  // (observed: two adds ending as quantity 1). So wait until two consecutive
  // snapshots are identical before computing and applying the merge.
  function merge(isRetry) {
    // A second trigger during a running pass (e.g. rapid double add) queues
    // one follow-up pass instead of being dropped.
    if (merging) {
      queued = true;
      return Promise.resolve(false);
    }
    merging = true;
    return stableCart(0)
      .then(function (cart) {
        if (!cart) return false;
        var groups = {};
        cart.items.forEach(function (item, index) {
          var key = groupKey(item);
          (groups[key] = groups[key] || []).push({ item: item, index: index });
        });
        var dupGroups = Object.keys(groups)
          .map(function (k) { return groups[k]; })
          .filter(function (g) {
            if (g.length < 2) return false;
            // Skip groups containing app-transform splits (identical full
            // signatures) — see fullSig().
            var seen = {};
            for (var i = 0; i < g.length; i++) {
              var s = fullSig(g[i].item);
              if (seen[s]) return false;
              seen[s] = true;
            }
            return true;
          });
        if (!dupGroups.length) return false;

        var quantities = cart.items.map(function (item) { return item.quantity; });
        dupGroups.forEach(function (g) {
          var total = g.reduce(function (s, e) { return s + e.item.quantity; }, 0);
          quantities[g[0].index] = total;
          g.slice(1).forEach(function (e) { quantities[e.index] = 0; });
        });
        return updateAll(quantities).then(function (res) {
          if (!res.ok) {
            if (!isRetry) setTimeout(function () { merge(true); }, 1200);
            return false;
          }
          // Total quantity must survive the merge; if the cart moved under us
          // regardless of the stability gate, hand it to one retry pass.
          return getCart().then(function (after) {
            if (after.item_count !== cart.item_count && !isRetry) {
              setTimeout(function () { merge(true); }, 1200);
            }
            return notifySurfaces().then(function () { return true; });
          });
        });
      })
      .catch(function () { return false; })
      .finally(function () {
        merging = false;
        if (queued) {
          queued = false;
          setTimeout(function () { merge(); }, 300);
        }
      });
  }

  window.RotimCartMerge = { merge: merge };

  // Optis performs its own add-to-cart fetch (it intercepts the button click
  // before the theme's product-form handler), so hooking product-form alone
  // misses those adds. Wrap fetch: any successful POST to /cart/add triggers a
  // merge pass shortly after, regardless of which script initiated the add.
  var origFetch = window.fetch;
  window.fetch = function () {
    var url = String(arguments[0] && arguments[0].url ? arguments[0].url : arguments[0]);
    var result = origFetch.apply(this, arguments);
    if (url.indexOf('/cart/add') !== -1) {
      result.then(function (res) {
        if (res.ok) setTimeout(merge, 400);
      }).catch(function () {});
    }
    return result;
  };

  // Cart page: clean up pre-existing duplicates once. notifySurfaces()
  // re-renders the item list through the theme's pubsub; fall back to a
  // reload when pubsub isn't available. No loop: a merged cart has no
  // duplicates on the next pass.
  function initCartPage() {
    if (!location.pathname.match(/\/cart\/?$/)) return;
    merge().then(function (changed) {
      if (changed && typeof publish !== 'function') location.reload();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartPage);
  } else {
    initCartPage();
  }
})();
